/**
 * Delete Invite Modal Component
 *
 * Confirmation modal for deleting an invite.
 *
 * @fileoverview Delete invite modal component
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdWarning } from 'react-icons/md';
import type { AdminInvite } from '../../types/admin';

interface DeleteInviteModalProps {
  invite: AdminInvite | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

/**
 * Delete Invite Modal Component
 *
 * Displays a confirmation dialog for permanently deleting an invite.
 */
export const DeleteInviteModal: React.FC<DeleteInviteModalProps> = ({
  invite,
  isOpen,
  onClose,
  onConfirm,
  loading,
}) => {
  const warningBg = useColorModeValue('red.50', 'red.900');
  const warningColor = useColorModeValue('red.600', 'red.200');

  if (!invite) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Invite</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box
            p={4}
            borderRadius="md"
            bg={warningBg}
            mb={4}
            display="flex"
            alignItems="flex-start"
            gap={3}
          >
            <Icon as={MdWarning} boxSize={5} color={warningColor} mt={0.5} />
            <Text fontSize="sm" color={warningColor}>
              This action cannot be undone. The invite record will be permanently deleted.
            </Text>
          </Box>
          <Text>
            Are you sure you want to delete the invite for{' '}
            <Text as="span" fontWeight="bold">{invite.email}</Text>?
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={onConfirm}
            isLoading={loading}
            loadingText="Deleting..."
          >
            Delete Invite
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteInviteModal;
